import { Picker } from './Picker'
import { ProgramChips, programLabel } from '@/lib/programs'
import { errorsFor, type FieldError } from '@/lib/schema'
import type { Task } from '@/lib/types'

export const DRAFT_STEPS = [
  { label: 'Task definition', hint: 'Title, programs and the request' },
  { label: 'Task brief', hint: 'Twelve questions about the work' },
  { label: 'Rubric', hint: 'What makes a result good' },
]
export const draftStepLabel = (step: number, scientific = false) =>
  scientific ? ['Workflow definition', 'Workflow brief', 'Rubric'][step] : DRAFT_STEPS[step].label

/** Shared by the draft editor and the introduction so labels and controls stay consistent. */
export function DraftSteps({ step, onChange, errors = new Set<number>(), scientific = false }: { step: number; onChange: (step: number) => void; errors?: Set<number>; scientific?: boolean }) {
  return (
    <nav className="wizard-nav" aria-label="Draft steps">
      {DRAFT_STEPS.map((s, i) => (
        <button key={s.label} type="button" className={`wizard-step ${step === i ? 'active' : ''} ${errors.has(i) ? 'has-error' : ''}`} aria-current={step === i ? 'step' : undefined} onClick={() => onChange(i)}>
          <span className="n">{i + 1}</span>
          <span><span className="lbl">{draftStepLabel(i, scientific)}</span><span className="hint-line">{scientific ? ['Title and applications', 'Scientific goal and useful variations', 'Shared success criteria'][i] : s.hint}</span></span>
        </button>
  ))}
</nav>
  )
}

export function TaskDefinitionFields({ title, programs, prompt, parentId, parents, preferred, errors = [], readOnly = false,
  onTitle, onPrograms, onPrompt, onParent }: {
  title: string; programs: string[]; prompt: string; parentId: string; parents: Task[]; preferred?: string[]
  errors?: FieldError[]; readOnly?: boolean; onTitle: (value: string) => void; onPrograms: (value: string[]) => void
  onPrompt: (value: string) => void; onParent: (value: string) => void
}) {
  return <>
    <label className="field"><span>Title</span><input type="text" readOnly={readOnly} value={title} onChange={(e) => onTitle(e.target.value)} placeholder="e.g. Prepare a measurement report" />
      {errorsFor(errors, 'title').map((m, i) => <div className="field-error" key={i}>{m}</div>)}</label>
    <div className="field"><span className="field-label">Programs used</span><em className="hint">Choose the programs needed for this task. You can update them during execution.</em><ProgramChips value={programs} onChange={onPrograms} preferred={preferred} disabled={readOnly} />
      {errorsFor(errors, 'programs').map((m, i) => <div className="field-error" key={i}>{m}</div>)}</div>
    <label className="field">
      <span>Is this task a continuation of another task?</span>
      <em className="hint">If you want to create this task starting from already existing work, please create the preliminary design as a separate task first. Only your tasks that have passed the draft review (stage 3 onwards) can be chosen.</em>
      <Picker label="Starting task" field value={parentId} onChange={onParent} disabled={readOnly || (parents.length === 0 && !parentId)}
        options={[{ value: '', label: 'No, this task stands on its own' }, ...parents.map(t => ({ value: t.task_id, label: t.title, detail: `${programLabel(t)} · ${t.stage_info.label}` }))]} />
      {parents.length === 0 && !parentId && <em className="hint">You have no tasks past the draft review yet, so there is nothing to continue from.</em>}
      {errorsFor(errors, 'parent_task_id').map((m, i) => <div className="field-error" key={i}>{m}</div>)}
    </label>
    {parentId && <div className="note info" style={{ marginBottom: '1rem' }}>This is expected to continue on the exact outcome of this task, like two tasks in the same project.</div>}
    <label className="field">
      <span>The request, written to a capable colleague</span>
      <em className="hint">This is the exact text the AI receives. Write it as you would ask a colleague, with the facts a real requester would give. Do not turn it into a step-by-step checklist.</em>
      <textarea readOnly={readOnly} value={prompt} onChange={(e) => onPrompt(e.target.value)} style={{ minHeight: 150 }}
        placeholder="Prepare a report from the supplied measurements. Include each sample, its value and unit, and the source file. Keep the original precision. Make the report clear enough for a colleague to compare the samples." />
      {errorsFor(errors, 'prompt').map((m, i) => <div className="field-error" key={i}>{m}</div>)}
    </label>
  </>
}
